import { Injectable } from '@angular/core';
import { ApplicationConfig, ProjectInfo, SystemTypes } from '@frontend-site/shared/data';
import { PersistenceFacade } from '@frontend-site/persistence';

const TM_ROUTE_PREFIX = '/tm';

function createPath(projectId: number, path: string, options?: { addTmPrefix: boolean }): string {
    const baseRoute = options?.addTmPrefix ? TM_ROUTE_PREFIX : '';
    return `${baseRoute}/${projectId}/${path}`;
}
function createPathWithTmPrefix(projectId: number, path: string): string {
    return createPath(projectId, path, { addTmPrefix: true });
}

@Injectable()
export class ProjectLandingPageProvider {
    constructor(private readonly uiPersistence: PersistenceFacade) {}

    getProjectLandingPath(projectInfo: ProjectInfo | null, appConfig?: ApplicationConfig): string {
        if (!projectInfo) {
            throw new Error('project info can`t be null');
        }

        if (this.isTestsProject(projectInfo)) {
            return getMyTestsReadyViewPath(projectInfo.id);
        }

        const lastProjectPagePath = this.uiPersistence.getGroupedLastPageVisited('project');
        if (lastProjectPagePath) {
            return lastProjectPagePath;
        }

        return getProjectFallbackPath(projectInfo, appConfig);
    }

    private isTestsProject(projectInfo: ProjectInfo): boolean {
        return projectInfo.hasTests && (!projectInfo.modular || projectInfo.hasModule('TESTS'));
    }
}

function getProjectFallbackPath(projectInfo: ProjectInfo, appConfig?: ApplicationConfig): string {
    if (projectInfo.modular && !projectInfo.hasModule('ANALYSIS_MANAGEMENT') && projectInfo.systemType === SystemTypes.SALESFORCE) {
        return createPathWithTmPrefix(projectInfo.id, `discovery`);
    }

    if (projectInfo.modular) {
        return createPathWithTmPrefix(projectInfo.id, `development/list`);
    }

    if (projectInfo.restrictedApp) {
        return createPathWithTmPrefix(projectInfo.id, `trialSetup/uploadPatch`);
    }

    if (projectInfo.isS4PrepProject() && projectInfo.hasActiveEtl) {
        return createPathWithTmPrefix(projectInfo.id, `reports/deletedTxnsReport`);
    }

    if (projectInfo.accountOnTrial && (projectInfo.isS4ConversionProject() || projectInfo.isIcr3Project()) && projectInfo.hasRequirements) {
        return createPathWithTmPrefix(projectInfo.id, `requirements/list`);
    }

    if (!projectInfo.hasActiveEtl && !projectInfo.etlProcessing) {
        return createPathWithTmPrefix(projectInfo.id, `trialSetup/noEtlMode`);
    }

    if (!projectInfo.hasActiveEtl && projectInfo.etlProcessing) {
        return createPathWithTmPrefix(projectInfo.id, `trialSetup/etlProcessing`);
    }

    if (projectInfo.accountOnTrial && !projectInfo.panayaUser) {
        return createPathWithTmPrefix(projectInfo.id, `trialSetup/systemOnTrial`);
    }

    if (projectInfo.projectManager) {
        if (projectInfo.isS4Project()) {
            if (projectInfo.isIcr3Project() && projectInfo.projectInProgress) {
                return createPath(projectInfo.id, `projectPlan`);
            }
            return createPathWithTmPrefix(projectInfo.id, `project/customDashboard`);
        }

        return createPathWithTmPrefix(projectInfo.id, `project/dashboard`);
    }

    if (projectInfo.hasTests) {
        return getMyTestsReadyViewPath(projectInfo.id);
    }

    if (projectInfo.hasCodeCorrections) {
        return getMyOpenCorrectionsViewPath(projectInfo.id);
    }

    if (projectInfo.rdxEnabled && projectInfo.hasDevModuleIssues) {
        return getMyDevOpenViewPath(appConfig?.userGuid.toString() ?? '', projectInfo.id);
    }

    return getDefaultDashboardPath(projectInfo.id);
}

function getMyTestsReadyViewPath(projectId: number): string {
    const myTestsReadyView = 'MY_TESTS_READY';
    return createPathWithTmPrefix(projectId, `execution/list/testsGrid?filters={}&view=${myTestsReadyView}`);
}

function getMyOpenCorrectionsViewPath(projectId: number): string {
    return createPath(projectId, `corrections/my-corrections`);
}

function getMyDevOpenViewPath(ownerId: string, projectId: number): string {
    const constants = {
        MY_DEV_OPEN_VIEW: 'MY_DEV_OPEN',
        REQUIREMENT_STATUS: { BACKLOG: 0, DESIGN: 1, DEVELOPMENT: 2 },
        DEFECT_STATUS: { OPEN: { ABSOLUTE_ID: 11 } }
    };
    const statuses = [
        constants.REQUIREMENT_STATUS.BACKLOG,
        constants.REQUIREMENT_STATUS.DESIGN,
        constants.REQUIREMENT_STATUS.DEVELOPMENT,
        constants.DEFECT_STATUS.OPEN.ABSOLUTE_ID
    ];
    const filters = JSON.stringify({
        status: statuses.join(','),
        owner: ownerId.toString()
    });
    return createPathWithTmPrefix(projectId, `development/list?filters=${filters}&view=${constants.MY_DEV_OPEN_VIEW}`);
}

function getDefaultDashboardPath(projectId: number): string {
    return createPathWithTmPrefix(projectId, 'project/dashboard');
}
